import React, { useContext, useEffect, useRef, useState } from "react"
import { useController } from "react-hook-form"
import {
    Dimensions,
    KeyboardAvoidingView,
    ScrollView,
    TouchableOpacity,
    View,
} from "react-native"
import { theme } from "../../theme/theme"
import { DimensionsContext, IS_WEB } from "../../utils/mobileUtils"
import FilledButtonM from "../button/FilledButtonM"
import TextButtonM from "../button/TextButtonM"
import TextInputM from "../input/TextInputM"
import { ListGroupM } from "../list/ListGroupM"
import { ListItemM } from "../list/ListItemM"
import { formStepName } from "./formStepName"
import ErrorTextM from "../text/ErrorTextM"
import TextM from "../text/TextM"
import TitleM from "../text/TitleM"
//import CameraImagesM from "../../camera/CameraImagesM";
import * as amplitude from "@amplitude/analytics-react-native"
import CameraImagesM from "@common/camera/CameraImagesM"
import { IconImageM } from "@common/components/icons/IconsM"
import { IconTextM } from "@common/components/icons/IconTextM"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { formIds } from "@data/constants/formConstants"
import { addMediaToField, plr } from "@data/utility/Utils"
import { ActivityIndicator, Switch, TouchableRipple } from "react-native-paper"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { useSelector } from "react-redux"
import DateTimePicker from "../input/DateTimePicker"
import ServerSearch from "../input/ServerSearch"
import { SelectInputM } from "../../../student/account/onboarding/SelectInputM"
import { PaginatedFlatListNoNavigation } from "../../screens/PaginatedFlatListNoNavigation"

export const FormStep = (props) => {
    // ** Component props
    const {
        data,
        control,
        setError,
        errors,
        goToNextStep,
        isLastStep,
        trigger,
        formValues,
        setFormValues,
        register,
        resetsToDefaultOnNext,
        hideNextButton,
        isStepDirty,
        champion,
        serverError,
        flex = 1,
    } = props

    const { field } = useController({
        control,
        name: data?.name ?? "",
    })
    const insets = useSafeAreaInsets()

    useEffect(() => {
        // L'étape en propriété, jamais dans le nom : un type par étape remplissait le plafond
        // de 2 000 types d'Amplitude (P-4823).
        amplitude.track("form.step.started", { step: formStepName(data) })
    }, [])

    // Initialize the field with the default value
    useEffect(() => {
        if (
            data?.defaultValue !== field.value &&
            (!field.value ||
                (Array.isArray(field.value) && field.value?.length === 0))
        ) {
            field.onChange(data?.defaultValue)
            setFormValues({
                ...control._formValues,
                [field?.name]: data?.defaultValue,
            })
        }
    }, [])

    const renderBottomComponent = () => {
        return (
            !!data?.bottomComponent && (
                <View
                    style={{
                        marginBottom: 20,
                        // Collé en bas de l'écran par défaut ; `bottomComponentInline`
                        // le pose juste sous l'entrée, sans vide entre les deux.
                        marginTop: data?.bottomComponentInline ? 0 : "auto",
                    }}
                >
                    {typeof data?.bottomComponent === "function"
                        ? data?.bottomComponent(
                              control,
                              goToNextStep,
                              errors,
                              formValues
                          )
                        : data?.bottomComponent}
                </View>
            )
        )
    }
    const renderTopComponent = () => {
        return !!data?.topComponent && <View>{data?.topComponent}</View>
    }
    const dimensions = useContext(DimensionsContext)

    const renderInput = () => {
        switch (data?.type) {
            case "select.single":
                if (data?.loading)
                    return (
                        <View
                            style={{
                                alignItems: "center",
                                marginTop: 20,
                                flex: 1,
                                justifyContent: "center",
                            }}
                        >
                            <ActivityIndicator
                                color={theme.colors.primary}
                                style={{ alignSelf: "center" }}
                            />
                        </View>
                    )
                if (data?.options?.length === 0)
                    return (
                        <View
                            style={{
                                alignItems: "center",
                                marginTop: 20,
                                flex: 1,
                                justifyContent: "center",
                            }}
                        >
                            {!!data?.emptyIconName && (
                                <FontAwesome5
                                    name={data?.emptyIconName ?? "times"}
                                    color={theme.colors.light}
                                    solid
                                    size={40}
                                />
                            )}
                            {!!data?.emptyIcon && (
                                <IconImageM
                                    source={data?.emptyIcon}
                                    width={40}
                                    style={{
                                        tintColor: theme.colors.light,
                                    }}
                                />
                            )}
                            <TextM
                                wrap
                                style={{
                                    textAlign: "center",
                                    marginTop: 10,
                                    marginHorizontal: 20,
                                }}
                                fontWeight={"Medium"}
                                color={theme.colors.light}
                            >
                                {data?.emptyMessage ?? "Aucun élément"}
                            </TextM>
                        </View>
                    )
                return (
                    <ListGroupM
                        options={data?.options}
                        initialScrollIndex={data?.initialVisibleIndex}
                        sectionMode={data?.sectionMode}
                        style={{ marginBottom: 70 }}
                        itemHeight={data?.height ? data?.height : 70}
                        renderSectionHeader={({
                            section: { title, isFirst = false },
                        }) => {
                            return (
                                <View
                                    style={{
                                        backgroundColor: theme.colors.lightMore,
                                        borderTopStartRadius: isFirst ? 14 : 0,
                                        borderTopEndRadius: isFirst ? 14 : 0,
                                        paddingLeft: 10,
                                        paddingRight: 10,
                                        paddingTop: 15,
                                        paddingBottom: 10,
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        flexDirection: "row",
                                    }}
                                >
                                    <TextM fontWeight={"Medium"}>{title}</TextM>
                                </View>
                            )
                        }}
                        renderItem={(item) => {
                            return (
                                <ListItemM
                                    isFirst={
                                        !data?.sectionMode && item.index === 0
                                    }
                                    isLast={
                                        !data?.sectionMode &&
                                        item.index === data?.options?.length - 1
                                    }
                                    onPress={() => {
                                        field.onChange(item.item?.value)
                                        setFormValues({
                                            ...control._formValues,
                                            [field?.name]: item.item?.value,
                                        })
                                        if (
                                            !isLastStep &&
                                            !data?.noAutoNextStep
                                        ) {
                                            goToNextStep()
                                        }
                                    }}
                                    itemHeight={
                                        data?.height ? data?.height : 70
                                    }
                                    selected={item.item?.value === field.value}
                                    option={item.item}
                                    disabled={item.item?.disabled}
                                />
                            )
                        }}
                    />
                )

            case "select.card.single":
                return (
                    <ScrollView
                        style={{
                            alignSelf: "center",
                            width: dimensions?.width,
                            height: "100%",
                        }}
                        contentContainerStyle={{
                            alignItems: "center",
                            justifyContent: "center",
                            paddingBottom: 40,
                        }}
                    >
                        {data?.options?.map((option, index) => {
                            const isSelected = option?.value === field?.value

                            return (
                                <TouchableRipple
                                    key={index}
                                    onPress={
                                        option?.disabled
                                            ? null
                                            : () => {
                                                  field.onChange(option?.value)
                                                  goToNextStep()
                                              }
                                    }
                                    style={{
                                        alignItems: "center",
                                        justifyContent: "center",
                                        alignSelf: "center",
                                        backgroundColor: !isSelected
                                            ? theme.colors.white
                                            : theme.colors.linkLighter,
                                        borderWidth: !isSelected ? 1 : 2,
                                        borderColor: !isSelected
                                            ? theme.colors.lightMore
                                            : theme.colors.primary,
                                        borderRadius: 15,
                                        width: dimensions?.width - 40,
                                        marginTop: 20,
                                    }}
                                    disabled={option?.disabled}
                                    underlayColor={theme.colors.lightMore}
                                >
                                    {React.createElement(
                                        data?.cardContentComponent,
                                        {
                                            labelData: option?.labelData,
                                        }
                                    )}
                                </TouchableRipple>
                            )
                        })}
                    </ScrollView>
                )

            case "select.grouped.single":
                return (
                    <ScrollView
                        style={{
                            alignSelf: "center",
                            width: dimensions?.width,
                            height: "100%",
                        }}
                        contentContainerStyle={{
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {data?.options?.map((option, index) => {
                            return (
                                option?.subOptions?.length > 0 && (
                                    <View
                                        key={index}
                                        style={{
                                            alignItems: "flex-start",
                                            justifyContent: "center",
                                            alignSelf: "center",
                                            backgroundColor: theme.colors.white,
                                            borderWidth: 1,
                                            borderColor: theme.colors.lightMore,
                                            borderRadius: 15,
                                            width: dimensions?.width - 40,
                                            marginTop: 20,
                                            padding: 10,
                                        }}
                                    >
                                        <TextM fontWeight="Bold" fontSize={18}>
                                            {option?.label}
                                        </TextM>
                                        <View
                                            style={{
                                                flexWrap: "wrap",
                                                flexDirection: "row",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                marginTop: 20,
                                                width: "100%",
                                            }}
                                        >
                                            {option?.subOptions?.map(
                                                (subOption, subIndex) => {
                                                    const isSelected =
                                                        subOption?.value ===
                                                        field?.value

                                                    return (
                                                        <TouchableRipple
                                                            key={subIndex}
                                                            onPress={() => {
                                                                field.onChange(
                                                                    subOption?.value
                                                                )
                                                                goToNextStep()
                                                            }}
                                                            style={{
                                                                alignItems:
                                                                    "center",
                                                                justifyContent:
                                                                    "center",
                                                                backgroundColor:
                                                                    isSelected
                                                                        ? theme
                                                                              .colors
                                                                              .linkLighter
                                                                        : theme
                                                                              .colors
                                                                              .white,
                                                                borderWidth:
                                                                    isSelected
                                                                        ? 2
                                                                        : 1,
                                                                borderColor:
                                                                    isSelected
                                                                        ? theme
                                                                              .colors
                                                                              .primary
                                                                        : theme
                                                                              .colors
                                                                              .lightMore,
                                                                borderRadius: 15,
                                                                paddingHorizontal: 25,
                                                                paddingVertical: 20,
                                                                margin: 5,
                                                            }}
                                                            underlayColor={
                                                                theme.colors
                                                                    .lightMore
                                                            }
                                                        >
                                                            <TextM
                                                                fontWeight={
                                                                    isSelected
                                                                        ? "Bold"
                                                                        : null
                                                                }
                                                                color={
                                                                    isSelected
                                                                        ? theme
                                                                              .colors
                                                                              .primary
                                                                        : null
                                                                }
                                                            >
                                                                {
                                                                    subOption?.label
                                                                }
                                                            </TextM>
                                                        </TouchableRipple>
                                                    )
                                                }
                                            )}
                                        </View>
                                    </View>
                                )
                            )
                        })}
                    </ScrollView>
                )

            case "select.multiple":
                useEffect(() => {
                    if (resetsToDefaultOnNext)
                        field.onChange(data?.defaultValue)
                }, [])

                return (
                    <ListGroupM
                        options={data?.options}
                        itemHeight={data?.height ? data?.height : 70}
                        sectionMode={data?.sectionMode}
                        style={{
                            alignSelf: "center",
                        }}
                        renderSectionHeader={({
                            section: { title, isFirst = false },
                        }) => {
                            return (
                                <View
                                    style={{
                                        backgroundColor: theme.colors.lightMore,
                                        borderTopStartRadius: isFirst ? 14 : 0,
                                        borderTopEndRadius: isFirst ? 14 : 0,
                                        paddingLeft: 10,
                                        paddingRight: 10,
                                        paddingTop: 15,
                                        paddingBottom: 10,
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        flexDirection: "row",
                                    }}
                                >
                                    <TextM fontWeight={"Medium"}>{title}</TextM>
                                </View>
                            )
                        }}
                        renderItem={(item) => {
                            const ordered = !!data?.ordered
                            const selectedIndex = (field.value || []).indexOf(
                                item.item?.value
                            )
                            const selected = selectedIndex !== -1
                            const currentCount = field.value?.length || 0
                            const maxSelection = data?.maxSelection
                            const canSelect =
                                selected ||
                                !maxSelection ||
                                currentCount < maxSelection
                            // Check if item is disabled (either by maxSelection or by option property)
                            const isItemDisabled =
                                item.item?.disabled && selected
                            const isDisabled = !canSelect || isItemDisabled

                            return (
                                <ListItemM
                                    isFirst={
                                        !data?.sectionMode && item.index === 0
                                    }
                                    isLast={
                                        !data?.sectionMode &&
                                        item.index === data?.options?.length - 1
                                    }
                                    onPress={() => {
                                        if (!canSelect || isItemDisabled) return

                                        const value = Array.isArray(field.value)
                                            ? field.value.slice()
                                            : []
                                        if (selected) {
                                            value.splice(selectedIndex, 1)
                                        } else {
                                            // Push at end to preserve order when ordered
                                            value.push(item.item?.value)
                                        }
                                        field.onChange(value)
                                        setFormValues({
                                            ...control._formValues,
                                            [field?.name]: value,
                                        })
                                        trigger()
                                    }}
                                    itemHeight={
                                        data?.height ? data?.height : null
                                    }
                                    selected={selected}
                                    option={{
                                        ...item.item,
                                        leftComponent:
                                            ordered && selected ? (
                                                <View
                                                    style={{
                                                        width: 22,
                                                        height: 22,
                                                        borderRadius: 11,
                                                        backgroundColor:
                                                            theme.colors
                                                                .primary,
                                                        alignItems: "center",
                                                        justifyContent:
                                                            "center",
                                                        marginRight: 10,
                                                    }}
                                                >
                                                    <TextM
                                                        fontWeight="Bold"
                                                        fontSize={14}
                                                        color={
                                                            theme.colors.white
                                                        }
                                                    >
                                                        {selectedIndex + 1}
                                                    </TextM>
                                                </View>
                                            ) : (
                                                item.item?.leftComponent
                                            ),
                                    }}
                                    disabled={isDisabled}
                                    style={{
                                        paddingVertical: 20,
                                        opacity: isDisabled ? 0.5 : 1,
                                    }}
                                />
                            )
                        }}
                    />
                )
            case "text.single":
                return (
                    <TextInputM
                        placeholder={data?.placeholder}
                        autofocus
                        name={data?.name}
                        control={control}
                        errors={errors}
                    />
                )

            case "photos": {
                const [images, setImages] = useState(
                    data.defaultValue?.map((x) => {
                        return {
                            uri: x,
                            width: IS_WEB
                                ? dimensions?.width * 0.4
                                : dimensions?.width * 0.6,
                            height: dimensions?.height * 0.4,
                        }
                    }) ?? []
                )

                useEffect(() => {
                    addMediaToField(field, images, true)

                    setFormValues({
                        ...control._formValues,
                        [field?.name]: images?.map((file) => {
                            return {
                                type: "image/png",
                                height: file?.height,
                                width: file?.width,
                                uri: file?.uri,
                            }
                        }),
                    })
                }, [images])

                return (
                    <CameraImagesM
                        images={images}
                        setImages={setImages}
                        style={{
                            marginTop: 20,
                            height: dimensions?.height * 0.6,
                        }}
                        formId={formIds.NEW_MATERIAL}
                        useGallery
                    />
                )
            }

            case "text.multiple": {
                const inputRefs = data?.inputs?.map(() => useRef())

                useEffect(() => {
                    if (!isStepDirty)
                        setTimeout(function () {
                            inputRefs[0]?.current?.focus()
                        }, 100)
                }, [])

                return (
                    <ScrollView style={{ paddingBottom: 100 }}>
                        {data?.inputs?.map((i, index) => {
                            return (
                                <React.Fragment key={index}>
                                    {!!i?.label && (
                                        <TitleM
                                            color={theme.colors.primary}
                                            style={{
                                                marginBottom: 10,
                                                marginTop: 10,
                                                textAlign: "left",
                                            }}
                                            fontWeight="ExtraBold"
                                        >
                                            {i?.label}
                                        </TitleM>
                                    )}
                                    <TextInputM
                                        {...register(
                                            i?.name,
                                            i?.validationRules
                                        )}
                                        onChangeAdditional={(value) => {
                                            setFormValues({
                                                ...control._formValues,
                                                [i?.name]: value,
                                            })
                                        }}
                                        disabled={i?.disabled}
                                        hidden={i?.hidden}
                                        setError={setError}
                                        liveCheck={i?.liveCheck}
                                        liveCheckPreRequest={
                                            i?.liveCheckPreRequest
                                        }
                                        placeholder={i?.placeholder}
                                        ref={inputRefs[index]}
                                        keyboardType={i?.keyboardType ?? null}
                                        inputMode={i?.inputMode ?? null}
                                        onSubmitEditing={() => {
                                            if (
                                                index <
                                                data?.inputs?.length - 1
                                            )
                                                inputRefs[
                                                    index + 1
                                                ].current?.focus()
                                        }}
                                        returnKeyType={
                                            index === data?.inputs?.length - 1
                                                ? "done"
                                                : "next"
                                        }
                                        multiline={!!i?.height}
                                        style={{
                                            height: i?.height ?? null,
                                        }}
                                        password={i?.password}
                                        key={index}
                                        control={control}
                                        errors={errors}
                                    />
                                </React.Fragment>
                            )
                        })}
                        {!!data?.checkBoxes &&
                            data?.checkBoxes?.map((cb, key) => {
                                const { field } = useController({
                                    control,
                                    name: cb?.name ?? "",
                                })

                                return (
                                    <View
                                        style={{
                                            flexDirection: "row",
                                            marginTop: 50,
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            marginHorizontal: 10,
                                        }}
                                        key={key}
                                    >
                                        <IconTextM
                                            text={cb?.label}
                                            style={{
                                                width: "70%",
                                                marginHorizontal: 0,
                                                marginTop: 0,
                                            }}
                                            icon={cb?.icon}
                                            color={theme.colors.primary}
                                        />
                                        <Switch
                                            {...register(
                                                cb?.name,
                                                cb?.validationRules
                                            )}
                                            color={theme.colors.primary}
                                            style={{ alignSelf: "center" }}
                                            value={field?.value}
                                            onValueChange={(value) => {
                                                field.onChange(value)
                                                setFormValues({
                                                    ...control._formValues,
                                                    [cb?.name]: value,
                                                })
                                            }}
                                        />
                                    </View>
                                )
                            })}
                    </ScrollView>
                )
            }

            case "select.two.oriented":
                return (
                    <View
                        style={{
                            alignSelf: "center",
                            width: "100%",
                            position: "absolute",
                            bottom: 20,
                        }}
                    >
                        <FilledButtonM
                            style={{
                                marginTop: 50,
                            }}
                            onPress={
                                data?.options[0]?.action
                                    ? data?.options[0]?.action
                                    : () => {
                                          field.onChange(
                                              data?.options[0]?.value
                                          )
                                          goToNextStep()
                                      }
                            }
                            color={data?.options[0]?.color}
                            label={data?.options[0]?.label}
                        />

                        <TextButtonM
                            style={{
                                marginTop: 10,
                                alignSelf: "center",
                            }}
                            color={data?.options[1]?.color}
                            onPress={() => {
                                field.onChange(data?.options[1]?.value)
                                goToNextStep()
                            }}
                            fontWeight="ExtraBold"
                            label={data?.options[1]?.label?.toUpperCase()}
                        />
                    </View>
                )
            case "select.paginated": {
                const flatListRef = useRef()
                const [urlParams, setUrlParams] = useState({
                    search: "",
                })

                const error = data?.service
                    ? useSelector(
                          (state) => state.api[data?.service?.name]?.error
                      )
                    : null

                const [paginatedOptions, setPaginatedOptions] = useState([])

                const initialSelectedItemsIds = data?.singleMode
                    ? [field.value]
                    : field.value
                const initialSelectedItems =
                    initialSelectedItemsIds
                        ?.map(
                            (id) =>
                                data?.defaultSelectedItems?.find(
                                    (item) => item?.id === id
                                ) ?? null
                        )
                        ?.filter((item) => !!item) ?? []

                const [selectedItems, setSelectedItems] =
                    useState(initialSelectedItems)

                const options = data?.withNull
                    ? [{ id: null, display_name: "Aucun" }].concat(
                          paginatedOptions
                      )
                    : paginatedOptions

                // Header component to display selected items
                const SelectedItemsHeader = React.memo(
                    ({ selectedItems }) =>
                        !data?.singleMode && (
                            <View
                                style={{
                                    flexWrap: "wrap",
                                    alignItems: "center",
                                }}
                            >
                                <TextM>
                                    {selectedItems.length}{" "}
                                    {plr("sélectionné", selectedItems.length)}
                                </TextM>
                                {selectedItems.map((item, index) =>
                                    renderItem(
                                        { item: item, index: index },
                                        true
                                    )
                                )}
                            </View>
                        )
                )
                SelectedItemsHeader.displayName = "SelectedItemsHeader"

                useApiStatus(
                    data?.service, // api service
                    false, // success message on toast (string or null)
                    false, // error message on toast (true) or keep it in redux state (false)
                    (successData) => {
                        if (successData?.meta?.current_page === 1) {
                            // Refresh
                            setPaginatedOptions(
                                successData?.[data?.responseKey]
                            )
                        } else {
                            // Concat
                            setPaginatedOptions(
                                paginatedOptions.concat(
                                    successData?.[data?.responseKey]
                                )
                            )
                        }
                    } // success callback
                )

                const renderItem = (item, header = false) => {
                    const selected = data?.singleMode
                        ? field.value === item.item?.id
                        : field.value?.includes(item.item?.id)
                    return (
                        <TouchableOpacity
                            disabled={
                                !!props.searchingRequest ||
                                (selected && !header)
                            }
                            key={item.index}
                            style={{
                                opacity: selected && !header ? 0.5 : 1,
                            }}
                            onPress={() => {
                                if (data?.singleMode) {
                                    field.onChange(item.item?.id)
                                    setSelectedItems([item.item])
                                    setFormValues({
                                        ...control._formValues,
                                        [field?.name]: item.item?.id,
                                    })
                                    if (!isLastStep) goToNextStep()
                                } else {
                                    const value = field.value
                                        ? field.value?.slice()
                                        : []
                                    if (selected) {
                                        value?.splice(
                                            value?.indexOf(item.item?.id),
                                            1
                                        )
                                        setSelectedItems(
                                            selectedItems.filter(
                                                (i) => i.id !== item.item.id
                                            )
                                        )
                                    } else {
                                        value?.push(item.item?.id)
                                        setSelectedItems(
                                            selectedItems.concat(item.item)
                                        )
                                    }
                                    field.onChange(value)
                                    setFormValues({
                                        ...control._formValues,
                                        [field?.name]: value,
                                    })
                                }

                                trigger()
                            }}
                        >
                            {data?.renderItem(item?.item, selected)}
                        </TouchableOpacity>
                    )
                }
                return (
                    <View
                        style={{
                            width: dimensions?.width,
                            alignSelf: "center",
                            flex: 1,
                            alignItems: "center",
                            paddingTop: 20,
                        }}
                    >
                        {!data?.noSearchBar && (
                            <ServerSearch
                                style={{
                                    width:
                                        dimensions?.width < 800
                                            ? dimensions?.width - 40
                                            : dimensions?.width / 2,
                                    margin: 10,
                                }}
                                urlParams={urlParams}
                                setUrlParams={setUrlParams}
                            />
                        )}
                        <ErrorTextM
                            errorData={error}
                            style={{ textAlign: "center", marginBottom: 20 }}
                        />

                        <PaginatedFlatListNoNavigation
                            ref={flatListRef}
                            ListHeaderComponent={
                                <SelectedItemsHeader
                                    selectedItems={selectedItems}
                                />
                            }
                            data={options}
                            additionalOnRefresh={() => {}}
                            additionalRefreshing={false}
                            requestApiService={data?.service}
                            requestRouteParams={data?.routeParams}
                            requestUrlParams={{
                                ...urlParams,
                                ...data?.additionalUrlParams,
                            }}
                            requestData={null}
                            renderItem={renderItem}
                        />
                    </View>
                )
            }
            case "date":
                return (
                    <DateTimePicker
                        type={data?.type}
                        control={control}
                        field={field}
                        setFormValues={setFormValues}
                        range={data?.range}
                        disabled={data?.disabled}
                    />
                )
            case "time":
                return (
                    <DateTimePicker
                        type={data?.type}
                        control={control}
                        field={field}
                        setFormValues={setFormValues}
                        disabled={data?.disabled}
                    />
                )
            case "date-time":
                return (
                    <DateTimePicker
                        type={data?.type}
                        control={control}
                        field={field}
                        setFormValues={setFormValues}
                        range={data?.range}
                        disabled={data?.disabled}
                    />
                )
            case "day-time":
                return (
                    <DateTimePicker
                        type={data?.type}
                        control={control}
                        field={field}
                        disabled={data?.disabled}
                        setFormValues={setFormValues}
                    />
                )
            case "date-multiple":
                return (
                    <View
                        style={{
                            flexDirection: "row",
                            alignSelf: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        {data?.inputs?.map((i, index) => {
                            const { field: localField } = useController({
                                control,
                                name: i?.name ?? "",
                            })

                            return (
                                <View
                                    key={index}
                                    style={{
                                        marginHorizontal: 20,
                                    }}
                                >
                                    {!!i?.title && (
                                        <TextM
                                            style={{
                                                marginBottom: 10,
                                                textAlign: "center",
                                                width: 140,
                                            }}
                                            fontWeight={"ExtraBold"}
                                            color={theme.colors.primary}
                                            fontSize={15}
                                            wrap
                                        >
                                            {i?.title}
                                        </TextM>
                                    )}
                                    <DateTimePicker
                                        type={i?.type}
                                        control={control}
                                        field={localField}
                                        setFormValues={setFormValues}
                                    />
                                </View>
                            )
                        })}
                    </View>
                )
            case "custom":
                return React.createElement(data.component, {
                    control,
                    goToNextStep,
                    errors,
                    register,
                    serverError,
                    formValues,
                    setFormValues,
                    field,
                    trigger,
                    setError,
                    showNextButton: data?.showNextButton,
                    ...data?.componentProps,
                })

            /* ------------------------------------------------------------------
             * New type : multi.select.single
             * Render several independent SelectInputM components in the same step
             * Each input config in data.inputs must contain :
             *   - name
             *   - label / sliderLabel
             *   - options (array)
             *   - defaultValue
             *   - hidden (optional boolean)
             *   - onChange (optional override)
             * ----------------------------------------------------------------*/
            case "multi.select.single":
                return (
                    <View
                        style={{
                            width: "100%",
                            alignItems: "center",
                        }}
                    >
                        {data?.inputs?.map((inputConf, idx) => {
                            const { field: localField } = useController({
                                control,
                                name: inputConf?.name ?? "",
                            })

                            // Evaluate conditional visibility
                            const evaluateCondition = (cond) => {
                                if (!cond) return true
                                if (typeof cond === "function") {
                                    try {
                                        return !!cond(control._formValues)
                                    } catch (e) {
                                        return true
                                    }
                                }
                                if (cond?.name) {
                                    const depVal =
                                        control._formValues?.[cond.name]
                                    if (
                                        Object.prototype.hasOwnProperty.call(
                                            cond,
                                            "is"
                                        )
                                    ) {
                                        return depVal === cond.is
                                    }
                                    if (
                                        Object.prototype.hasOwnProperty.call(
                                            cond,
                                            "in"
                                        )
                                    ) {
                                        return (cond.in || []).includes(depVal)
                                    }
                                }
                                return true
                            }

                            const isVisible =
                                evaluateCondition(inputConf?.visibleWhen) &&
                                !inputConf?.hidden
                            if (!isVisible) return null

                            const isDisabled =
                                !evaluateCondition(inputConf?.enabledWhen) ||
                                inputConf?.disabled === true

                            const shouldUseSwitch =
                                inputConf?.renderAs === "switch"

                            if (shouldUseSwitch) {
                                const options =
                                    inputConf?.options?.length === 2
                                        ? inputConf?.options
                                        : [
                                              { label: "Oui", value: true },
                                              { label: "Non", value: false },
                                          ]
                                const [optA, optB] = options
                                const switchValue =
                                    localField.value === optA?.value
                                const isHighlighted =
                                    inputConf?.lightBg === true
                                const containerStyle = {
                                    width: dimensions?.width - 40,
                                    marginBottom: 16,
                                    flexDirection: "row",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    backgroundColor: isHighlighted
                                        ? theme.colors.primaryLighter
                                        : undefined,
                                    padding: isHighlighted ? 12 : 12,
                                    borderRadius: isHighlighted ? 12 : 0,
                                    opacity: isDisabled ? 0.6 : 1,
                                }
                                return (
                                    <View key={idx} style={containerStyle}>
                                        <TextM
                                            style={{
                                                marginRight: 10,
                                                flex: 1,
                                            }}
                                        >
                                            {inputConf?.label}
                                        </TextM>
                                        <Switch
                                            disabled={isDisabled}
                                            color={theme.colors.primary}
                                            value={switchValue}
                                            onValueChange={(val) => {
                                                const newValue = val
                                                    ? optA?.value
                                                    : optB?.value
                                                localField.onChange(newValue)
                                                setFormValues({
                                                    ...control._formValues,
                                                    [inputConf?.name]: newValue,
                                                })
                                                if (inputConf?.onChange) {
                                                    inputConf.onChange(newValue)
                                                }
                                            }}
                                        />
                                    </View>
                                )
                            }

                            return (
                                <SelectInputM
                                    key={idx}
                                    options={inputConf?.options}
                                    label={inputConf?.label}
                                    sliderLabel={
                                        inputConf?.sliderLabel ??
                                        inputConf?.label
                                    }
                                    name={inputConf?.name}
                                    value={localField.value}
                                    onSelect={(value) => {
                                        localField.onChange(value)
                                        setFormValues({
                                            ...control._formValues,
                                            [inputConf?.name]: value,
                                        })
                                        if (inputConf?.onChange) {
                                            inputConf.onChange(value)
                                        }
                                    }}
                                    style={{
                                        marginBottom: 20,
                                        width: dimensions?.width - 40,
                                        opacity: isDisabled ? 0.6 : 1,
                                    }}
                                />
                            )
                        })}
                    </View>
                )
            default:
                return <></>
        }
    }

    const renderDescription = () => {
        if (React.isValidElement(data?.description)) {
            return data.description
        }
        return (
            <TextM
                style={{
                    marginBottom: 10,
                    marginRight: 10,
                    textAlign: "justify",
                }}
                wrap
            >
                {data?.description}
            </TextM>
        )
    }

    return (
        <>
            {!champion && (
                <View
                    style={{
                        width:
                            flex !== 1
                                ? Dimensions.get("window").width * flex
                                : dimensions?.width * flex,
                        alignItems: "flex-start",
                        justifyContent: "flex-start",
                        paddingHorizontal: 20,
                        paddingBottom: 10,
                    }}
                >
                    {!!data?.label && (
                        <TitleM
                            color={
                                data?.type === "select.two.oriented"
                                    ? theme.colors.grey
                                    : theme.colors.primary
                            }
                            style={{
                                marginBottom: 10,
                                marginTop:
                                    data?.type === "select.two.oriented"
                                        ? 40
                                        : 0,
                                textAlign:
                                    data?.type === "select.two.oriented"
                                        ? "center"
                                        : "left",
                            }}
                            fontWeight="ExtraBold"
                        >
                            {data?.label}
                        </TitleM>
                    )}
                    {!!data?.description && renderDescription()}
                </View>
            )}
            <KeyboardAvoidingView
                style={{
                    flex: 1,
                    width: "100%",
                    alignItems: "center",
                }}
                keyboardVerticalOffset={
                    !champion ? -(300 + insets.top) : -(250 + insets.top)
                }
                behavior={"position"}
            >
                <View
                    style={{
                        flex: 1,
                        width:
                            flex !== 1
                                ? Dimensions.get("window").width * flex
                                : dimensions?.width * flex,
                        paddingHorizontal: 20,
                        marginBottom: hideNextButton ? 0 : 70,
                    }}
                >
                    {renderTopComponent()}

                    {!!data?.icon && (
                        <View
                            style={{
                                marginTop: 80,
                                alignSelf: "center",
                            }}
                        >
                            {data?.icon}
                        </View>
                    )}
                    {renderInput()}
                    {data?.name && errors?.[data.name] && (
                        <ErrorTextM
                            errorData={errors[data.name]}
                            style={{
                                textAlign: "center",
                                marginVertical: 10,
                            }}
                        />
                    )}
                    {!!data?.indication && data?.indication !== "" && (
                        <TextM
                            fontSize={12}
                            wrap
                            style={{
                                marginTop: 10,
                                marginBottom: 10,
                                textAlign: IS_WEB ? "center" : "justify",
                            }}
                        >
                            {data?.indication}
                        </TextM>
                    )}
                    {isLastStep && (
                        <ErrorTextM
                            errorData={props.serverError}
                            style={{ marginBottom: 20 }}
                        />
                    )}
                    {renderBottomComponent()}
                </View>
            </KeyboardAvoidingView>
        </>
    )
}
